import { z } from "zod"

import { branchIdSchema, entityIdSchema, legalEntityIdSchema } from "./identifiers.js"

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/)

export const cutoverImportKindV1Schema = z.enum([
  "master_data",
  "active_work",
  "open_ar_ap",
  "opening_balance",
  "inventory_opening",
])

export const cutoverBatchCreateV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema.nullable(),
  importKind: cutoverImportKindV1Schema,
  sourceSystem: z.string().trim().min(2).max(80),
  sourceExportId: z.string().trim().min(1).max(140),
  sourceExportSha256: sha256Schema,
}).strict()

export const cutoverRecordStageV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  sourceRecordType: z.string().trim().min(1).max(80),
  sourceRecordId: z.string().trim().min(1).max(140),
  sourceVersion: z.string().trim().min(1).max(80),
  sourceRecordSha256: sha256Schema,
  payloadSha256: sha256Schema,
  payload: z.record(z.string(), z.unknown()),
}).strict()

export const cutoverBatchValidateV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
}).strict()

export const cutoverBatchApproveV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  reason: z.string().trim().min(12).max(500),
}).strict()

export const cutoverMasterActiveCommitV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
}).strict()

export const cutoverMasterActiveResultV1Schema = z.object({
  entityType: z.literal("cutover_adapter_run"),
  entityId: entityIdSchema,
  state: z.enum(["review_required", "committed"]),
  version: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  committedCount: z.number().int().min(0).max(5_000),
  duplicateCount: z.number().int().min(0).max(5_000),
  conflictCount: z.number().int().min(0).max(5_000),
  reviewRunId: entityIdSchema,
}).strict()

export const cutoverOpenFinancePreviewV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  configurationId: entityIdSchema,
  configurationVersion: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  postingDate: z.string().date(),
}).strict()

export const cutoverOpenFinanceCommitV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  previewId: entityIdSchema,
  previewFingerprint: sha256Schema,
}).strict()

const cutoverOpenAmountSummaryV1Schema = z.object({
  grossMinor: z.number().int().nonnegative(),
  paymentMinor: z.number().int().nonnegative(),
  creditMinor: z.number().int().nonnegative(),
  openMinor: z.number().int().nonnegative(),
}).strict()

export const cutoverOpenFinancePreviewResultV1Schema = z.object({
  entityType: z.literal("cutover_finance_preview"),
  entityId: entityIdSchema,
  state: z.enum(["ready", "blocked"]),
  version: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  previewFingerprint: sha256Schema,
  receivable: cutoverOpenAmountSummaryV1Schema,
  payable: cutoverOpenAmountSummaryV1Schema,
  opening: z.object({
    debitMinor: z.number().int().nonnegative(),
    creditMinor: z.number().int().nonnegative(),
    lineCount: z.number().int().nonnegative(),
    residualLineCount: z.number().int().nonnegative(),
  }).strict(),
  deferredInventory: z.object({
    assetAccount: z.string().min(1).max(140),
    offsetAccount: z.string().min(1).max(140),
    valuationMinor: z.number().int().positive(),
  }).strict().nullable(),
  archiveFingerprint: sha256Schema,
  blockers: z.array(z.string()).max(20),
}).strict()

export const cutoverOpenFinanceCommitResultV1Schema = z.object({
  entityType: z.literal("cutover_adapter_run"),
  entityId: entityIdSchema,
  state: z.literal("committed"),
  version: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  previewId: entityIdSchema,
  previewFingerprint: sha256Schema,
  committedCount: z.number().int().positive().max(5_000),
  receivableMinor: z.number().int().nonnegative(),
  payableMinor: z.number().int().nonnegative(),
  openingJournalEntry: z.string().min(1).max(140),
  archiveFingerprint: sha256Schema,
}).strict()

export const cutoverInventoryOpeningPreviewV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  financePreviewId: entityIdSchema,
  financePreviewFingerprint: sha256Schema,
  postingDate: z.string().date(),
}).strict()

export const cutoverInventoryOpeningCommitV1Schema = z.object({
  cutoverVersion: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  previewId: entityIdSchema,
  previewFingerprint: sha256Schema,
}).strict()

export const cutoverInventoryOpeningPreviewResultV1Schema = z.object({
  entityType: z.literal("inventory_opening_preview"),
  entityId: entityIdSchema,
  state: z.enum(["ready", "blocked"]),
  version: z.literal(1),
  batchId: entityIdSchema,
  validationFingerprint: sha256Schema,
  financePreviewId: entityIdSchema,
  financePreviewFingerprint: sha256Schema,
  previewFingerprint: sha256Schema,
  itemCount: z.number().int().positive().max(5_000),
  warehouseCount: z.number().int().positive().max(5_000),
  quantityMilli: z.number().int().positive(),
  valuationMinor: z.number().int().positive(),
  assetAccount: z.string().min(1).max(140),
  offsetAccount: z.string().min(1).max(140),
  blockers: z.array(z.string()).max(20),
}).strict()

export const cutoverInventoryOpeningCommitResultV1Schema = z.object({
  entityType: z.literal("inventory_opening_commit"),
  entityId: entityIdSchema,
  state: z.literal("committed"),
  version: z.literal(1),
  batchId: entityIdSchema,
  previewId: entityIdSchema,
  previewFingerprint: sha256Schema,
  stockReconciliation: z.string().min(1).max(140),
  itemCount: z.number().int().positive().max(5_000),
  warehouseCount: z.number().int().positive().max(5_000),
  quantityMilli: z.number().int().positive(),
  valuationMinor: z.number().int().positive(),
}).strict()

export const cutoverResultV1Schema = z.object({
  entityType: z.literal("cutover_batch"),
  entityId: entityIdSchema,
  state: z.enum(["staging", "validated", "approved"]),
  version: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema.nullable(),
  importKind: cutoverImportKindV1Schema,
  recordCount: z.number().int().min(0).max(5_000),
  validationFingerprint: sha256Schema.nullable(),
  stagedRecordId: entityIdSchema.nullable(),
  deduplicated: z.boolean(),
  readyForAdapterCommit: z.boolean(),
}).strict()

export type CutoverBatchCreateV1 = z.infer<typeof cutoverBatchCreateV1Schema>
export type CutoverRecordStageV1 = z.infer<typeof cutoverRecordStageV1Schema>
export type CutoverBatchValidateV1 = z.infer<typeof cutoverBatchValidateV1Schema>
export type CutoverBatchApproveV1 = z.infer<typeof cutoverBatchApproveV1Schema>
export type CutoverMasterActiveCommitV1 = z.infer<
  typeof cutoverMasterActiveCommitV1Schema
>
export type CutoverMasterActiveResultV1 = z.infer<
  typeof cutoverMasterActiveResultV1Schema
>
export type CutoverOpenFinancePreviewV1 = z.infer<
  typeof cutoverOpenFinancePreviewV1Schema
>
export type CutoverOpenFinanceCommitV1 = z.infer<
  typeof cutoverOpenFinanceCommitV1Schema
>
export type CutoverOpenFinancePreviewResultV1 = z.infer<
  typeof cutoverOpenFinancePreviewResultV1Schema
>
export type CutoverOpenFinanceCommitResultV1 = z.infer<
  typeof cutoverOpenFinanceCommitResultV1Schema
>
export type CutoverInventoryOpeningPreviewV1 = z.infer<
  typeof cutoverInventoryOpeningPreviewV1Schema
>
export type CutoverInventoryOpeningCommitV1 = z.infer<
  typeof cutoverInventoryOpeningCommitV1Schema
>
export type CutoverInventoryOpeningPreviewResultV1 = z.infer<
  typeof cutoverInventoryOpeningPreviewResultV1Schema
>
export type CutoverInventoryOpeningCommitResultV1 = z.infer<
  typeof cutoverInventoryOpeningCommitResultV1Schema
>
export type CutoverResultV1 = z.infer<typeof cutoverResultV1Schema>
